import React from 'react';

import sharedStyles from 'features/authentication/auth-page.module.scss';
import {
    BACKEND_INACTIVE_ACCOUNT_ERROR_CODE,
    BACKEND_USER_NOT_ACTIVE_ERROR_CODE,
    BACKEND_INVALID_CREDENTIALS_ERROR_CODE,
    BACKEND_RECAPTCHA_ERROR_CODE,
    BACKEND_UNKNOWN_ERROR_CODE,
    BACKEND_INTERNET_CONN_ERROR_CODE,
} from 'features/authentication/utils/constants';
import type { BaseUserAuth, Dictionary } from '@placer-ui/types';
import { isMac } from 'features/mobile-report/utils/utils';
import { isProduction, isStaging } from 'utils/detect-environment';
import { setCookie } from 'utils/cookies/utils';

const placerTitleSuffix = ' - Placer';

export const setDocumentTitle = (title: string): void => {
    document.title = title + placerTitleSuffix;
};

export const emailFormErrorMessagesText: Dictionary<string> = {
    BACKEND_INVALID_CREDENTIALS_ERROR_CODE:
        'Your account is not active yet. The activation email sent to your email again. ' +
        'Please contact support@placer.ai for assistance.',
    BACKEND_RECAPTCHA_ERROR_CODE:
        'reCAPTCHA protection failed. Please refresh the page and try again. ' +
        'Contact support@placer.ai if issue repeats.',
    BACKEND_UNKNOWN_ERROR_CODE: 'Something went wrong... Please try again later.',
    BACKEND_INTERNET_CONN_ERROR_CODE: 'Please check your internet connection.',
    BACKEND_INACTIVE_ACCOUNT_ERROR_CODE:
        // eslint-disable-next-line quotes
        "Your email is not associated with an SSO Account.\nPlease request access from your company's admin.",
    BACKEND_USER_NOT_ACTIVE_ERROR_CODE:
        "Seems like your account wasn't verified yet. " +
        'Please complete the verification process by clicking the link in the email we sent to your mailbox.',
};

export const sendEmailErrorMessagesText: Dictionary<string> = {
    [BACKEND_INVALID_CREDENTIALS_ERROR_CODE]:
        emailFormErrorMessagesText.BACKEND_INVALID_CREDENTIALS_ERROR_CODE,
    [BACKEND_RECAPTCHA_ERROR_CODE]: emailFormErrorMessagesText.BACKEND_RECAPTCHA_ERROR_CODE,
    [BACKEND_UNKNOWN_ERROR_CODE]: emailFormErrorMessagesText.BACKEND_UNKNOWN_ERROR_CODE,
    [BACKEND_INTERNET_CONN_ERROR_CODE]: emailFormErrorMessagesText.BACKEND_INTERNET_CONN_ERROR_CODE,
    [BACKEND_INACTIVE_ACCOUNT_ERROR_CODE]:
        emailFormErrorMessagesText.BACKEND_INACTIVE_ACCOUNT_ERROR_CODE,
    [BACKEND_USER_NOT_ACTIVE_ERROR_CODE]:
        emailFormErrorMessagesText.BACKEND_USER_NOT_ACTIVE_ERROR_CODE,
};

export const EmailFormErrorCodeMessages: { [key: number]: JSX.Element } = {
    [BACKEND_INVALID_CREDENTIALS_ERROR_CODE]: (
        <span className={sharedStyles.backendErrorMessage}>
            {emailFormErrorMessagesText.BACKEND_INVALID_CREDENTIALS_ERROR_CODE}
        </span>
    ),
    [BACKEND_RECAPTCHA_ERROR_CODE]: (
        <span className={sharedStyles.backendErrorMessage}>
            {emailFormErrorMessagesText.BACKEND_RECAPTCHA_ERROR_CODE}
        </span>
    ),
    [BACKEND_UNKNOWN_ERROR_CODE]: (
        <span className={sharedStyles.backendErrorMessage}>
            {emailFormErrorMessagesText.BACKEND_UNKNOWN_ERROR_CODE}
        </span>
    ),
    [BACKEND_INTERNET_CONN_ERROR_CODE]: (
        <span className={sharedStyles.backendErrorMessage}>
            {emailFormErrorMessagesText.BACKEND_INTERNET_CONN_ERROR_CODE}
        </span>
    ),
    [BACKEND_INACTIVE_ACCOUNT_ERROR_CODE]: (
        <span className={sharedStyles.backendErrorMessage}>
            {emailFormErrorMessagesText.BACKEND_INACTIVE_ACCOUNT_ERROR_CODE}
        </span>
    ),
    [BACKEND_USER_NOT_ACTIVE_ERROR_CODE]: (
        <span className={sharedStyles.backendErrorMessage}>
            {emailFormErrorMessagesText.BACKEND_USER_NOT_ACTIVE_ERROR_CODE}
        </span>
    ),
};

export const webViewRedirectLink = (endpoint: string) => {
    if (isStaging()) {
        return `https://staging-analytics-app.placer.team${endpoint}`;
    } else if (isProduction()) return `https://analytics-app.placer.ai${endpoint}`;
    else return `http://${isMac ? 'localhost' : '10.0.2.2'}:9000${endpoint}`;
};

export const openAppLink = (user: BaseUserAuth, next_url: string | null) => {
    const decodedNextUrl = next_url && decodeURIComponent(next_url);
    const userToSend = decodedNextUrl ? { ...user, next_url: decodedNextUrl } : user;

    return `placer://?token=${JSON.stringify(userToSend)}`;
};

export const setDataOriginCookie = () => {
    const cookieExpirationDate = new Date(Date.now() + 24 * 60 * 60 * 1000);
    const cookieDomain = window.location.hostname.split('.').slice(-2).join('.');

    setCookie({
        key: 'data-origin',
        value: 'signup',
        expires: cookieExpirationDate,
        domain: cookieDomain,
        path: '/',
    });
};

const nextUrlToIgnoreForMobile = ['https://www.placer.ai', 'https://placer.ai'];

export const shouldIgnoreMobileFlow = (nextUrl: string | null) => {
    return nextUrlToIgnoreForMobile.some((urlToIgnore) => nextUrl?.startsWith(urlToIgnore));
};
